import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import connectApi from '../../api/connectApi'
import {
    ConnectUserType,
    constants,
    fetchProductPictures,
    getConnectUser
} from '../../helpers'
import { sendMessageToWorker } from '../../helpers/communication'
import { HomeInterface, HomeReducerStateType } from '../../storeTypes'
import { ErrorResponse } from '../../types'
import { VeType } from '../../storeTypes/vesTypes'
import * as Sentry from '@sentry/react'
import { veDepositsAction } from 'features/veDeposits/veDepositSlice'

const { VES, VE_DEPOSIT, COUNTRY_CONFIG, ENROLLMENT } = constants.endpoints

const homeActionQueries = async (veCode: string, country: string) => {
    let appQueries = []
    let pageQueries = []

    const veDataQuery = connectApi.get(VES, {
        params: {
            code: veCode
        }
    })

    const veDepositQuery = connectApi.get(`${VE_DEPOSIT}${veCode}`)

    const countryConfigQuery = connectApi.get(COUNTRY_CONFIG, {
        params: {
            country
        }
    })

    appQueries = [veDataQuery, veDepositQuery, countryConfigQuery]

    const settledResults = await Promise.allSettled(appQueries)

    const [veData, veDeposit, countryConfig] = settledResults
    if (
        veData.status !== 'fulfilled' ||
        veDeposit.status !== 'fulfilled' ||
        countryConfig.status !== 'fulfilled'
    ) {
        throw new Error('Failed to fetch required data')
    }

    // The connectApi interceptor resolves HTTP error responses as fulfilled
    // promises so callers can inspect `.status`. Check for HTTP errors here.
    if (
        veData.value.status >= 300 ||
        veDeposit.value.status >= 300 ||
        countryConfig.value.status >= 300
    ) {
        throw new Error('Failed to fetch required data')
    }

    const { data: veDataResponse } = veData.value
    if (!Array.isArray(veDataResponse) || !veDataResponse[0]) {
        throw new Error('Invalid VE data response')
    }
    const veDataResult = veDataResponse[0]

    const rankingQuery = connectApi.get(VES, {
        params: {
            zone_code: veDataResult.zone_code
        }
    })

    const enrollmentPackagesQuery = connectApi.get(ENROLLMENT, {
        params: {
            zone_code: veDataResult.zone_code
        }
    })

    pageQueries = [rankingQuery, enrollmentPackagesQuery]

    const [rankingData, enrollmentPackagesData] = await Promise.allSettled(
        pageQueries
    )

    return {
        countryConfig: countryConfig.value,
        veDataResult,
        veDeposit: veDeposit.value,
        rankingData,
        enrollmentPackagesData
    }
}

const mergeUserAndVeData = (
    connectUserLocalStorage: ConnectUserType,
    veDataResult: VeType
) => {
    const {
        ve_villages: veVillages,
        zone,
        zone_name: zoneName,
        zone_code: zoneCode,
        ...restVeData
    } = veDataResult
    const user = {
        ...connectUserLocalStorage,
        veVillages,
        zoneSFID: zone,
        zoneName,
        zoneCode,
        ...restVeData
    }
    return user
}

const initialState: HomeReducerStateType = {
    userData: {},
    loading: false,
    error: '',
    rankingData: {},
    enrollmentData: {},
    veCode: ''
}

type HomeActionReturnType = {
    homeData: {}
    rankingData: {}
    enrollmentData: {}
    depositData: {}
    veCode: string
}

export const homeAction = createAsyncThunk<
    HomeActionReturnType,
    HomeInterface,
    { rejectValue: string }
>(
    'home/homeAction',
    async (
        { veCode, country }: HomeInterface,
        { rejectWithValue, dispatch }
    ) => {
        try {
            const connectUserLocalStorage = getConnectUser()
            const {
                countryConfig,
                veDataResult,
                veDeposit,
                rankingData,
                enrollmentPackagesData
            } = await homeActionQueries(veCode, country)

            const user = mergeUserAndVeData(
                connectUserLocalStorage,
                veDataResult
            )

            sendMessageToWorker({ 'user info': user })

            localStorage.setItem(constants.CONNECT_USER, JSON.stringify(user))
            localStorage.setItem(
                'country_config',
                JSON.stringify(countryConfig.data)
            )

            if (veDeposit?.data) {
                sendMessageToWorker({
                    [constants.MESSAGES.STORE_VE_DEPOSIT]: {
                        veDeposit: veDeposit.data,
                        veCode
                    }
                })

                dispatch(
                    veDepositsAction.fulfilled(
                        { veDeposit: veDeposit.data, veCode },
                        'home-prefetch',
                        { veCode }
                    )
                )
            }

            sendMessageToWorker({
                [constants.MESSAGES.STORE_VES]: {
                    ves: [veDataResult],
                    zoneCode: veDataResult.zone_code
                }
            })

            const enrollmentPackages = () => {
                if (
                    enrollmentPackagesData.status === 'fulfilled' &&
                    enrollmentPackagesData.value?.status < 400 &&
                    enrollmentPackagesData?.value?.data
                ) {
                    fetchProductPictures(
                        enrollmentPackagesData.value.data[0]?.product_list.map(
                            (product: { image_url: string }) =>
                                product.image_url
                        )
                    )

                    const productCategoryPayload =
                        enrollmentPackagesData?.value?.data?.[0]
                            ?.product_list ?? []

                    if (productCategoryPayload.length) {
                        sendMessageToWorker({
                            [constants.MESSAGES.STORE_PRODUCT_CATEGORY]:
                                productCategoryPayload
                        })
                    }

                    return enrollmentPackagesData.value.data
                }
                return null
            }

            return {
                homeData: { ...veDataResult, ...veDeposit?.data },
                rankingData:
                    rankingData.status === 'fulfilled' &&
                    rankingData.value?.status < 400
                        ? rankingData.value.data
                        : null,
                enrollmentData: enrollmentPackages(),
                depositData: veDeposit?.data || null,
                veCode
            }
        } catch (error: unknown) {
            Sentry.captureException(error)
            if (error instanceof ErrorResponse) {
                return rejectWithValue(error.response.data.detail)
            } else {
                return rejectWithValue('Un problème est survenu')
            }
        }
    }
)

export const homeSlice = createSlice({
    name: 'home',
    initialState,
    reducers: {
        resetHomeState: () => initialState
    },
    extraReducers: (builder) => {
        builder
            .addCase(homeAction.pending, (state) => {
                state.loading = true
                state.error = ''
            })
            .addCase(homeAction.fulfilled, (state, action) => {
                state.loading = false
                state.userData = action.payload.homeData
                state.error = ''
                state.rankingData = action.payload.rankingData
                state.enrollmentData = action.payload.enrollmentData
                state.veCode = action.payload.veCode
            })
            .addCase(homeAction.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload || 'Un problème est survenu'
            })
    }
})

export const { resetHomeState } = homeSlice.actions
export default homeSlice.reducer
